import { useCallback, useRef } from 'react';

/** One physical click reaches the handler several times: keep the first. */
const DEDUPE_MS = 100;

export interface PlotBands {
  /** Category of each band, left to right. */
  domain: string[];
  /** Canvas x where the first band starts (coordinate x + left padding). */
  left: number;
  /** Total width of the bands in canvas pixels. */
  width: number;
}

/**
 * The ONE place that reads G2 internals (undocumented, they may change with
 * the library version): the first view's x band scale domain and the
 * coordinate's `x`, `paddingLeft` and `width`. Returns `null` when any piece
 * is missing or not a usable number, so callers never act on a guess.
 * `@ant-design/charts` wraps the G2 chart in `.chart`.
 */
export const readPlotBands = (chart: any): PlotBands | null => {
  try {
    const view = (chart?.chart ?? chart)?.getContext?.()?.views?.[0];
    const domain = view?.scale?.x?.getOptions?.()?.domain;
    const options = view?.coordinate?.getOptions?.();
    if (!Array.isArray(domain) || !domain.length || !options) return null;

    const { x = 0, paddingLeft = 0, width } = options;
    if (![x, paddingLeft, width].every(Number.isFinite) || !(width > 0)) {
      return null;
    }

    return { domain, left: x + paddingLeft, width };
  } catch {
    return null;
  }
};

/**
 * Day under a click on the plot, from the chart's own x band scale.
 *
 * G2 hands the handler a payload whose `type` is the NATIVE event ("click"),
 * never "element:click", so the event name cannot tell a bar hit from a click
 * on empty plot. The raw payload (`nativeEvent` is the DOM event object, with
 * `offsetX` in canvas pixels) is emitted for every click, so a click anywhere
 * on a day's column area resolves through the band scale. Returns `null`
 * outside the plot or when the chart internals are not there.
 */
export const dayAtOffset = (chart: any, offsetX: unknown): string | null => {
  if (typeof offsetX !== 'number') return null;
  const bands = readPlotBands(chart);
  if (!bands) return null;

  const ratio = (offsetX - bands.left) / bands.width;
  if (!(ratio >= 0 && ratio < 1)) return null;

  return bands.domain[Math.floor(ratio * bands.domain.length)] ?? null;
};

/**
 * A stable `onEvent` for @ant-design/charts that reports the day a user
 * clicked as a datum with a `date`.
 *
 * Two sources, whichever arrives first wins (see `DEDUPE_MS`): the raw click
 * resolved by position (works on thin or empty columns) and the element
 * payload (`nativeEvent === true` with the bar's datum).
 *
 * The chart registers its handler once, when it mounts, so a plain closure
 * would keep calling the first render's callback (stale selection, stale
 * data). The latest callback lives in a ref and the handler never changes.
 */
export const useChartClick = (onDatum: (datum: any) => void) => {
  const latest = useRef(onDatum);
  latest.current = onDatum;
  const lastFiredAt = useRef(-Infinity);

  return useCallback((chart: unknown, event: any) => {
    if (!event || event.type !== 'click') return;

    let datum: any = null;
    if (event.nativeEvent === true) {
      datum = event.data?.data?.date ? event.data.data : null;
    } else {
      const date = dayAtOffset(chart, event.offsetX);
      datum = date ? { date } : null;
    }
    if (!datum) return;

    const now = Date.now();
    if (now - lastFiredAt.current < DEDUPE_MS) return;
    lastFiredAt.current = now;
    latest.current(datum);
  }, []);
};
